import React, { useCallback, useContext, useMemo } from 'react'
import {
  Typography,
  Chip,
  Box,
  Grid,
  useTheme,
  CheckboxCircle,
  PlayRectangleWithoutGradientIcon,
  ChipVariants,
  ConnectionsBadgeIcon,
  WhiteTooltip,
  InfoOutlinedIcon
} from '@hermes/web-components'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { AppDataContext } from '../../../../providers/AppData'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { useGetSearchProfileData } from '../../../../hooks/useGetProfileData'
import { PracticeOrSpecialistProfile, PracticeProfile, SpecialistProfile, StatisticTreatment } from '../../../../types'
import { useGetUnitsByLocale } from '../../../../hooks/useGetUnitsByLocale'
import useStatistic from '../../../../hooks/useStatistic'
import { AppointmentType, FilterType, PROFILE_REVIEW_TOTAL_LIMIT } from '../../../../constants/search'
import {
  getAddressFromPractices,
  transformAddressForSearchToString,
  transformHomeCareAddressToString
} from '../../../../utils/address'
import getChipColorConfiguration from '../../../../utils/getChipColorConfiguration'
import { StyledPlayVideoButtonMobile } from '../../../profiles/Intro/styles'
import { TEST_IDS } from '../../../../__tests__/testUtils/constants'
import { useVisibilityComponents } from '../../../../hooks/useVisibilityComponents'
import SearchAppointmentModal from '../../../search/SearchAppointmentModal'
import { getDefaultCountry } from '../../../../constants/global'
import {
  SearchProfileCardContainer,
  StyledAvatar,
  TopKeywordText,
  SponsoredBanner,
  NameTypography,
  SearchProfileCardContent
} from './SearchProfileCardMobileForced.styles'
import RatePaperMobileForcedMobile from './RatePaperMobileForcedMobile'
import { getTreatments } from '../../../../utils/getTreatments'

type GetLinkReviewInfoProps = {
  specialist?: SpecialistProfile
  practice?: PracticeProfile
  locale: string
  profileReviewTotal: number
}

const getLinkReviewInfo = ({ practice, specialist, locale, profileReviewTotal }: GetLinkReviewInfoProps) => {
  const link = practice ? `/${locale}/practice/${practice?.slug}` : `/${locale}/specialist/${specialist?.slug}`
  const linkReviewsPage = profileReviewTotal > PROFILE_REVIEW_TOTAL_LIMIT ? '/reviews/page-2' : '#reviews'
  const linkReviews = practice
    ? `/${locale}/practice/${practice?.slug}${linkReviewsPage}`
    : `/${locale}/specialist/${specialist?.slug}${linkReviewsPage}`

  return { link, linkReviews }
}

type TreatmentProps = {
  keyword: StatisticTreatment
}

const Treatment = ({ keyword }: TreatmentProps) => {
  const theme = useTheme()
  const additionalColorStyle = getChipColorConfiguration(keyword.variant)
  const keywordProps = keyword.href
    ? { component: 'a', href: keyword.href, variant: keyword.variant as ChipVariants }
    : { component: 'div', variant: keyword.variant as ChipVariants }

  return (
    <Grid item>
      <Chip
        {...keywordProps}
        label={keyword.label + (keyword.count ? ` (${keyword.count})` : '')}
        icon={
          keyword.variant === 'primary' ? (
            <CheckboxCircle
              sx={{ marginLeft: '0 !important', marginRight: '0 !important', width: '20px', height: '20px' }}
            />
          ) : (
            <></>
          )
        }
        sx={{
          maxWidth: 250,
          fontWeight: '500',
          cursor: 'pointer',
          '&:hover': {
            background: theme.palette.system[`${keyword.variant}200`]
          },
          ...(theme?.custom?.common?.treatmentChip ?? {}),
          ...additionalColorStyle,
          color: keyword?.variant ? '#002E2A' : '#1A3586',
          ...(keyword?.backgroundColor ? { backgroundColor: keyword.backgroundColor } : {})
        }}
      />
    </Grid>
  )
}

function SearchProfileCardMobileForced({
  specialist,
  practice,
  handleSetVideoPlayerModalData,
  isSponsor
}: PracticeOrSpecialistProfile) {
  const introductionVideo = (specialist || practice).media?.[0]
  const visibilityComponents = useVisibilityComponents({ specialist, practice })
  const translate = useTranslateMessage()
  const router = useRouter()

  const { language, locale } = useContext(AppDataContext)
  const defaultCountry = getDefaultCountry(locale)
  const {
    state: {
      filters: { selectedKeyword, appointmentType, distance: filterDistance },
      specialty
    }
  } = useContext(SearchProfilesPageContext)

  const profileData = useGetSearchProfileData({
    ...(practice ? { practice } : { specialist }),
    language,
    specialty
  })

  const {
    profileAvatarLink,
    profileAbbreviation,
    averageRating,
    practices,
    distance,
    isUnclaimed,
    profileReviewTotal,
    profileFullName,
    haveVideoConsultation,
    topLevelKeyword,
    transformedAddress,
    yearsOfExperienceValue,
    isPractice,
    externalId,
    shouldShowContactButton,
    slug
  } = profileData

  const handleOpenIntroductionVideoPlayer = () => {
    window.dataLayer.push({
      event: 'src_pg_prf_vid_click',
      cont_prf_name: profileFullName,
      cont_uuid: externalId,
      cont_prf_type: isPractice ? 'Practice' : 'Specialist'
    })
    handleSetVideoPlayerModalData({
      url: introductionVideo?.link,
      practiceId: practice?.id,
      specialistId: specialist?.id,
      mediaId: introductionVideo?.id
    })
  }

  const getUnitsByLocale = useGetUnitsByLocale()

  const locationString = useMemo(() => {
    const addressToTransform = getAddressFromPractices(practices, true)
    const isOnlyVideoConsultation = practices.length === 1 && practices[0].slug === FilterType.VIDEO_CONSULTATION
    const filterDistanceNumber = Number(filterDistance)

    if (
      appointmentType === AppointmentType.VIDEO_CONSULTATION ||
      isOnlyVideoConsultation ||
      (haveVideoConsultation && filterDistanceNumber < distance)
    ) {
      return translate('videoConsultation.availableOnline')
    }

    if (specialist?.type === 'homecare' && specialist?.address) {
      return `${getUnitsByLocale(specialist.distance, 2).distanceWithUnits} | ${transformHomeCareAddressToString(
        specialist?.address,
        language
      )}`
    }

    if (!addressToTransform) {
      return ''
    }

    return `${getUnitsByLocale(distance, 2).distanceWithUnits} | ${transformAddressForSearchToString(
      addressToTransform,
      language
    )}`
  }, [
    practices,
    appointmentType,
    haveVideoConsultation,
    filterDistance,
    distance,
    specialist?.type,
    specialist?.address,
    language,
    getUnitsByLocale,
    specialist?.distance,
    translate
  ])

  const { mergedStatistic, reviewStatistic } = useStatistic(specialist?.statistic || practice?.statistic || [])

  const treatments = useMemo(
    () =>
      getTreatments({
        specialist,
        practice,
        mergedStatistic,
        reviewStatistic,
        language,
        specialty,
        selectedKeyword
      }),
    [specialist, practice, mergedStatistic, reviewStatistic, language, specialty, selectedKeyword]
  )

  const lengthKeywords = specialist?.statisticLength ?? practice?.statisticLength

  const { link } = getLinkReviewInfo({ practice, specialist, locale, profileReviewTotal })

  const finalTreatments = useMemo(() => {
    if (!lengthKeywords || lengthKeywords <= 0) {
      return treatments
    }
    return [
      ...treatments,
      {
        variant: '',
        label: `+${lengthKeywords}`,
        href: link,
        backgroundColor: '#EAEEFC'
      }
    ]
  }, [treatments, lengthKeywords, link])

  const activeRelatedKeyword = specialty

  const saveActiveKeywordInStorage = () => {
    if (activeRelatedKeyword.id !== 1 && activeRelatedKeyword?.name?.[language]) {
      localStorage.setItem(`activeKeyword-${slug}`, activeRelatedKeyword.name[language])
    }
  }

  const renderTreatment = useMemo(
    () => () =>
      finalTreatments.map((keyword) => {
        return <Treatment key={keyword.label} keyword={keyword} />
      }),
    [finalTreatments]
  )

  const handleOpenProfilePage = useCallback(() => router.push(link), [router, link])

  return (
    <SearchProfileCardContainer
      container
      width="100%"
      wrap="nowrap"
      gap="16px"
      data-testid={TEST_IDS.SUGGESTED_SEARCH_RESULT_CARD}
      onClick={handleOpenProfilePage}
      onMouseDown={saveActiveKeywordInStorage}
      onAuxClick={(e) => {
        if (e.button === 1) {
          saveActiveKeywordInStorage()
          window.open(link, '_blank')
        }
      }}
    >
      <SearchProfileCardContent gap={2}>
        <Grid
          container
          wrap="nowrap"
          gap="12px"
          sx={{
            position: 'relative',
            padding: '16px 16px 0 16px'
          }}
        >
          <Box>
            <Box height="min-content" position="relative">
              {isSponsor && (
                <WhiteTooltip
                  placement="bottom-end"
                  title={translate('search-profile.sponsored.tooltip')}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      padding: '12px 16px',
                      maxWidth: '354px',
                      width: '100%',
                      height: 'auto',
                      fontSize: '11px',
                      lineHeight: '14px'
                    }
                  }}
                >
                  <SponsoredBanner>
                    {translate('search-profile.sponsored')} <InfoOutlinedIcon sx={{ fontSize: 14, color: '#112971' }} />
                  </SponsoredBanner>
                </WhiteTooltip>
              )}
              <StyledAvatar
                alt={profileFullName}
                data-testid={TEST_IDS.SUGGESTED_SEARCH_RESULT_CARD_AVATAR}
                width={80}
                height={80}
                preload
                src={profileAvatarLink}
              >
                {profileAbbreviation}
              </StyledAvatar>
              {visibilityComponents.introductionVideoAvailable && !!introductionVideo?.link.length ? (
                <StyledPlayVideoButtonMobile
                  variant="ghost"
                  data-testid={TEST_IDS.SUGGESTED_SEARCH_RESULT_CARD_VIDEO_BUTTON}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenIntroductionVideoPlayer()
                  }}
                >
                  <PlayRectangleWithoutGradientIcon viewBox="0 0 45 24" sx={{ width: '51px', height: '24px' }} />
                </StyledPlayVideoButtonMobile>
              ) : null}
            </Box>
          </Box>
          <Grid item container direction="column" gap={{ xs: '2px', lg: '6px' }} sx={{ minWidth: 0 }}>
            <Grid container pt={0}>
              <NextLink
                href={link}
                prefetch={false}
                style={{ textDecoration: 'none', minWidth: '100%', maxWidth: '80%' }}
                onClick={(e) => e.preventDefault()}
                onMouseDown={saveActiveKeywordInStorage}
              >
                <Typography
                  fontWeight={700}
                  fontSize="24px"
                  lineHeight="26px"
                  fontFamily="ES Klarheit Kurrent"
                  color="text.darkNavy"
                  data-testid={TEST_IDS.SUGGESTED_SEARCH_RESULT_CARD_FULL_NAME}
                  component="h2"
                  sx={{
                    width: '100%'
                  }}
                >
                  <NameTypography
                    component="span"
                    width={'100%'}
                    style={{
                      fontWeight: 500,
                      paddingRight: '24px',
                      minHeight: '48px',
                      lineHeight: '130%',
                      fontFamily: 'Poppins'
                    }}
                    fontFamily="ES Klarheit Kurrent"
                  >
                    {profileFullName}
                  </NameTypography>
                  <WhiteTooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          height: 'auto !important',
                          width: 'auto !important'
                        }
                      }
                    }}
                    sx={{
                      display: 'inline'
                    }}
                    title={isUnclaimed ? translate('profile.unverified') : translate('profile.verified')}
                  >
                    <Box component="span" display="inline">
                      <ConnectionsBadgeIcon
                        sx={{ width: '16px', height: '16px', marginLeft: '-16px' }}
                        fill={isUnclaimed ? '#CC4F4A' : '#1A3586'}
                        innerStroke="#FFF"
                        withCross={isUnclaimed}
                      />
                    </Box>
                  </WhiteTooltip>
                </Typography>
              </NextLink>
            </Grid>
            <Box
              display="flex"
              alignItems="start"
              flexDirection="column"
              data-testid={TEST_IDS.SUGGESTED_SEARCH_RESULT_CARD_KEYWORD}
            >
              {!practice && <TopKeywordText>{topLevelKeyword}</TopKeywordText>}
            </Box>
          </Grid>
        </Grid>
        <Box
          width="100%"
          height="100%"
          sx={{
            boxSizing: 'border-box'
          }}
        >
          <RatePaperMobileForcedMobile
            peerRecommendationsCount={specialist?.peerRecommendationsCount}
            averageRating={averageRating}
            isPractice={!!practice}
            profileReviewTotal={profileReviewTotal}
            yearsOfExperienceValue={yearsOfExperienceValue}
            location={practice ? transformedAddress || defaultCountry : locationString}
            treatments={renderTreatment()}
          >
            <Box onClick={(e) => e.stopPropagation()}>
              {shouldShowContactButton && (
                <SearchAppointmentModal
                  isUnclaimed={isUnclaimed}
                  totalReviews={profileReviewTotal}
                  specialist={specialist}
                  practice={practice}
                  isSearchPage
                  profileFullName={profileFullName}
                  isPractice={isPractice}
                  externalId={externalId}
                />
              )}
            </Box>
          </RatePaperMobileForcedMobile>
        </Box>
      </SearchProfileCardContent>
    </SearchProfileCardContainer>
  )
}

export default SearchProfileCardMobileForced
